import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { match } from 'ts-pattern'

import { LimitOrderItem } from '@root/entities/limit-order/ui/mobile/limit-order-item'
import { LimitOrderItemSkeleton } from '@root/entities/limit-order/ui/mobile/limit-order-item/limit-order-item-skeleton'
import {
  openCancelLimitOrderDrawerEvent,
  openEditLimitOrderDrawerEvent,
} from '@root/features/trade/limit-orders'
import {
  orderStatusAtom,
  tradingOrdersModelAtom,
} from '@root/screens/trading/atoms/trading-orders'
import { useTranslation } from '@root/shared/localization'
import { TradingList } from '@root/shared/trading'
import { CandleChartIcon } from '@root/shared/ui-kit'

import type { LimitOrderModel } from '@root/entities/limit-order'

type Props = {}

export const OrdersList: React.FC<Props> = () => {
  const { t } = useTranslation()

  const [ordersAtom] = useAtom(tradingOrdersModelAtom)
  const [ordersState] = useAtom(ordersAtom)
  const [orderStatus] = useAtom(orderStatusAtom)
  const [hasMore] = useAtom(ordersAtom.hasMoreItems)
  const [loadMoreState] = useAtom(ordersAtom.loadMoreState)

  const retry = useAction(ordersAtom.load)
  const loadMore = useAction(ordersAtom.loadMore)

  const openEditDrawer = useAction(openEditLimitOrderDrawerEvent)
  const openCancelDrawer = useAction(openCancelLimitOrderDrawerEvent)

  const emptyTitle = useMemo(
    () =>
      match(orderStatus)
        .with('active', () => t('trading.orders.no-orders.active'))
        .with('completed', () => t('trading.orders.no-orders.completed'))
        .with('expired', () => t('trading.orders.no-orders.expired'))
        .with('cancelled', () => t('trading.orders.no-orders.cancelled'))
        .with('failed', () => t('trading.orders.no-orders.failed'))
        .with(undefined, () => t('trading.orders.no-orders.title'))
        .exhaustive(),
    [orderStatus, t],
  )

  return (
    <>
      <TradingList<LimitOrderModel>
        emptyIcon={<CandleChartIcon />}
        state={ordersState}
        getKey={(it) => it.id}
        emptyTitle={emptyTitle}
        emptyText=" "
        renderItem={(it) => (
          <LimitOrderItem
            onEdit={() =>
              openEditDrawer({
                order: it,
              })
            }
            onCancel={() =>
              openCancelDrawer({
                orderId: it.id,
              })
            }
            key={it.network}
            order={it}
          />
        )}
        renderSkeleton={() => <LimitOrderItemSkeleton />}
        onRetry={retry}
        onLoadMore={loadMore}
        hasMore={hasMore}
        loadMoreState={loadMoreState}
      />
    </>
  )
}
